import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useGoalMentorSuggestions } from '@/hooks/useGoalMentorSuggestions';

interface SuggestedMentorsSectionProps {
  goalTitle: string;
  eTypes: string[];
}

export const SuggestedMentorsSection = ({ goalTitle, eTypes }: SuggestedMentorsSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { suggestedMentors, isLoading } = useGoalMentorSuggestions(goalTitle, eTypes);

  // Don't render if no suggestions
  if (!isLoading && suggestedMentors.length === 0) return null;

  return (
    <div className="border-t border-border/50 pt-4 mt-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full text-left text-sm font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
      >
        <Users className="w-4 h-4" />
        <span>Suggested Mentors</span>
        <Badge variant="secondary" className="ml-1 text-xs">
          {suggestedMentors.length}
        </Badge>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 ml-auto" />
        ) : (
          <ChevronDown className="w-4 h-4 ml-auto" />
        )}
      </button>

      {isOpen && (
        <div className="mt-3 space-y-2">
          {suggestedMentors.map(mentor => (
            <div
              key={mentor.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border/30"
            >
              <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                {mentor.bio && (
                  <p className="text-sm text-foreground truncate">{mentor.bio}</p>
                )}
                <div className="flex flex-wrap gap-1 mt-1">
                  {mentor.matchingSkills.map(skill => (
                    <Badge key={skill} variant="outline" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
                {mentor.yearsExperience && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {mentor.yearsExperience}+ years experience
                  </p>
                )}
              </div>
            </div>
          ))}

          <Button
            variant="ghost"
            size="sm"
            className="w-full text-primary"
            onClick={() => navigate('/dashboard', { state: { activeTab: 'mentorship' } })}
          >
            View all on Mentorship
          </Button>
        </div>
      )}
    </div>
  );
};
